import { MAX_SLIMES } from '../../engine/core/constants';
import type { GameContext } from '../../engine/core/GameContext';
import type { PressureState } from '../pressure/PressureState';

/**
 * 把待爆的史莱姆按空间相邻分成「环」——同一环在同一瞬间一起爆开，放大按环内个数。
 *
 * 环的窗口锚定分量内**最早**到达临界的成员。若锚定最晚者，每加入一个新成员都会把引爆
 * 时刻往后推，密集堆积反而永远等不到引爆。
 *
 * 泛洪用预分配缓冲，候选取自空间网格，不在热循环创建临时对象、也不做 O(n²) 全表扫描。
 */
export class RingResolver {
  private readonly queue = new Int32Array(MAX_SLIMES);
  private readonly visited = new Uint8Array(MAX_SLIMES);
  /** 全部环的成员按环连续排列。 */
  readonly members = new Int32Array(MAX_SLIMES);
  /** 第 i 个环在 members 中的起点与个数。 */
  readonly ringOffset = new Int32Array(MAX_SLIMES);
  readonly ringSize = new Int32Array(MAX_SLIMES);
  /** 第 i 个环的窗口起点（固定步序），取分量内最早到达临界者。 */
  readonly ringWindowStart = new Float32Array(MAX_SLIMES);
  ringCount = 0;

  private context!: GameContext;
  private pressure!: PressureState;
  private current = -1;
  private queueTail = 0;
  private memberCount = 0;
  private earliest = 0;

  private readonly visit = (other: number): void => {
    const { slimes } = this.context;
    if (this.visited[other] !== 0 || slimes.active[other] === 0 || slimes.isDying(other)) return;
    if (this.pressure.pending[other] === 0) return;
    const a = this.current;
    const dx = slimes.x[other] - slimes.x[a];
    const dy = slimes.y[other] - slimes.y[a];
    // 相邻判据与溅射范围同源：能被溅到的才可能属于同一环。
    const limit = (slimes.radius[a] + slimes.radius[other]) * this.context.level.world.splashRadius * this.context.catalog.slimes[slimes.definition[a]].splashRadius * 0.6;
    if (dx * dx + dy * dy >= limit * limit) return;
    this.visited[other] = 1;
    this.queue[this.queueTail++] = other;
    this.members[this.memberCount++] = other;
    if (this.pressure.pendingAt[other] < this.earliest) this.earliest = this.pressure.pendingAt[other];
  };

  resolve(context: GameContext, pressure: PressureState): number {
    this.context = context;
    this.pressure = pressure;
    const { slimes, grid } = context;
    this.visited.fill(0);
    this.ringCount = 0;
    this.memberCount = 0;

    for (let seed = 0; seed < slimes.capacity; seed += 1) {
      // 已标记死亡的仍然 active，要等 DespawnSlime 落地才回收。不排除的话它们会在下一步
      // 被重新解析成环并再溅射一次。
      if (slimes.active[seed] === 0 || slimes.isDying(seed) || pressure.pending[seed] === 0 || this.visited[seed] !== 0) continue;
      const offset = this.memberCount;
      this.visited[seed] = 1;
      this.queue[0] = seed;
      this.members[this.memberCount++] = seed;
      this.queueTail = 1;
      this.earliest = pressure.pendingAt[seed];

      for (let head = 0; head < this.queueTail; head += 1) {
        const id = this.queue[head];
        this.current = id;
        const reach = (slimes.radius[id] + slimes.maxActiveRadius) * context.level.world.splashRadius * context.catalog.slimes[slimes.definition[id]].splashRadius * 0.6;
        grid.forEachNearby(slimes.x[id], slimes.y[id], reach, this.visit);
      }

      this.ringOffset[this.ringCount] = offset;
      this.ringSize[this.ringCount] = this.memberCount - offset;
      this.ringWindowStart[this.ringCount] = this.earliest;
      this.ringCount += 1;
    }
    return this.ringCount;
  }
}
